import { Action } from "~/modules/actions/Action";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { getLogger, LoggerName } from "~/modules/logger";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { AnalyticsImpl } from "~/modules/telemetry/AnalyticsImpl";
import { sendWorkerTrackingEvent, toCauseMessage } from "~/modules/telemetry/utils";
import { EVENTS } from "~/modules/telemetry/Analytics";
import { ErrorCode, ErrorSeverity } from "~/modules/error";
import { Paginator, RestPaginator } from "~/modules/actions/Paginator";
import { createFlexSdkError } from "~/modules/error/ThrowError/ErrorHelper";
import { ContentAndApprovalsServiceImpl } from "~/backend/generated/ContentAndApprovals/api/contentAndApprovals.service";
import { ContentAndApprovals as RawContentAndApprovals } from "~/backend/generated/ContentAndApprovals/model/contentAndApprovals";
import { mapRootKeysToCamelCase } from "~/utils/mapKeys";





export interface ApprovalRequests {
    name?: string | null;
    category?: string | null;
    contentType?: string | null;
    status?: string | null;
    rejectionReason?: string | null;
    allowCategoryChange?: boolean | null;
}





export interface ContentTemplate {
    sid?: string | null;
    accountSid?: string | null;
    friendlyName?: string | null;
    language?: string | null;
    variables?: object | null;
    types?: object | null;
    approvalRequests?: ApprovalRequests | null;
    dateCreated?: Date | null;
    dateUpdated?: Date | null;
}





export interface GetContentTemplatesOptions {
    
    pageSize?: number;
    
    pageToken?: string;
    
    sortByDate?: string;
    
    sortByContentName?: string;
    
    dateCreatedAfter?: string;
    
    dateCreatedBefore?: string;
    
    contentName?: string;
    
    content?: string;
    
    language?: string[];
    
    contentType?: string[];
    
    channelEligibility?: string[];
}

function toContentTemplate(raw: RawContentAndApprovals): ContentTemplate {
    const root = mapRootKeysToCamelCase(raw) as Record<string, unknown>;
    const ar = root.approvalRequests ? mapRootKeysToCamelCase(root.approvalRequests as object) : undefined;
    return {
        ...(root as ContentTemplate),
        approvalRequests: ar as ApprovalRequests | undefined,
        variables: raw.variables,
        types: raw.types
    };
}






























export class GetContentTemplates implements Action<Promise<Paginator<ContentTemplate>>> {
    


    readonly #options?: GetContentTemplatesOptions;

    constructor(options?: GetContentTemplatesOptions) {
        this.#options = options;
    }

    private async fetchPage(context: ContextManager, pageToken?: string): Promise<RestPaginator<ContentTemplate>> {
        const logger = getLogger(context)(LoggerName.Actions);
        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);
        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();
        const worker = taskRouter.worker;

        const service = context.retrieveInstanceOf(
            ContextInstance.ContentAndApprovalsServiceImpl,
            ContentAndApprovalsServiceImpl
        );

        const {
            pageSize,
            sortByDate,
            sortByContentName,
            dateCreatedAfter,
            dateCreatedBefore,
            contentName,
            content,
            language,
            contentType,
            channelEligibility
        } = this.#options ?? {};

        try {
            const response = await service.listContentAndApprovals(
                pageSize,
                undefined,
                pageToken,
                sortByDate,
                sortByContentName,
                dateCreatedAfter,
                dateCreatedBefore,
                contentName,
                content,
                language,
                contentType,
                channelEligibility
            );

            const rawItems = response.contents ?? [];
            const items = rawItems.map((item) => toContentTemplate(item));

            const nextPageToken = response.meta?.next_page_url
                ? new URL(response.meta.next_page_url).searchParams.get("PageToken") ?? ""
                : "";
            const prevPageToken = response.meta?.previous_page_url
                ? new URL(response.meta.previous_page_url).searchParams.get("PageToken") ?? ""
                : "";

            sendWorkerTrackingEvent(EVENTS.GetContentTemplatesCompleted, analytics, worker, {
                pageSize,
                hasPageToken: !!pageToken
            });

            return new RestPaginator<ContentTemplate>(
                items,
                (token: string) => this.fetchPage(context, token),
                prevPageToken,
                nextPageToken
            );
        } catch (error) {
            const errorMsg = `GetContentTemplates: Failed to retrieve content templates`;
            sendWorkerTrackingEvent(EVENTS.GetContentTemplatesCompleted, analytics, worker, {
                error: `${errorMsg} (${toCauseMessage(error)})`,
                pageSize,
                hasPageToken: !!pageToken
            });
            logger.error(errorMsg, error);
            return Promise.reject(
                createFlexSdkError(context)(
                    ErrorCode.FailedToRetrieveContentTemplates,
                    { severity: ErrorSeverity.Error },
                    errorMsg,
                    error
                )
            );
        }
    }

    async run(ctx: {}): Promise<Paginator<ContentTemplate>> {
        return this.fetchPage(ctx as ContextManager, this.#options?.pageToken);
    }
}
